import * as React from "react"

import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"

const textareaVariants = cva([
  "min-h-20 w-full rounded-md px-2 py-1.5",
  "text-sm text-coval-text placeholder:text-coval-text-muted",
  "bg-surface-default",
  "transition-colors duration-150",
  "cursor-text",
  "disabled:cursor-default read-only:cursor-default",
  "border border-surface-1",
  "disabled:opacity-50",
  "resize-y",
])

// Composer container used when start/end adornments are provided. Matches the
// input surface/radius/border conventions, and draws the focus ring itself
// (.coval-field) since the textarea is nested.
const textareaContainerVariants = cva(
  [
    "coval-field w-full rounded-md px-2 py-1.5",
    "bg-surface-default",
    "transition-colors duration-150",
    "border border-surface-1",
    "cursor-text",
    "has-[textarea:disabled]:cursor-default has-[textarea:read-only]:cursor-default",
    "has-[textarea:disabled]:opacity-50",
  ],
  {
    variants: {
      variant: {
        default: "",
        // A primary-action composer: the 3:1 control outline on all sides,
        // softened by a larger radius and padding. Focus darkens the outline
        // to the active edge in place of the offset focus ring, which returns
        // in forced-colors mode where both edges take one system color.
        prominent: [
          "rounded-2xl px-5 pt-4 pb-3",
          "border-coval-border-control",
          "focus-within:border-coval-border-control-active",
          "not-forced-colors:has-[textarea:focus-visible]:outline-none!",
        ],
      },
    },
    defaultVariants: { variant: "default" },
  },
)

const composerTextareaVariants = cva(
  [
    "w-full resize-none bg-transparent outline-none",
    "text-sm text-coval-text placeholder:text-coval-text-muted",
  ],
  {
    variants: {
      variant: {
        default: "",
        prominent: "text-base",
      },
    },
    defaultVariants: { variant: "default" },
  },
)

type AccessibleName =
  | { "aria-label": string; "aria-labelledby"?: string }
  | { "aria-label"?: string; "aria-labelledby": string }

type TextareaVariant = NonNullable<
  VariantProps<typeof textareaContainerVariants>["variant"]
>

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  AccessibleName & {
    error?: boolean
    /** Visual treatment of the composer container (composer mode only). */
    variant?: TextareaVariant
    /**
     * Content rendered at the start (left) of a toolbar below the textarea.
     * Providing either adornment switches the textarea into composer mode: the
     * field and toolbar share a single bordered, focus-within container.
     */
    startAdornment?: React.ReactNode
    /** Content rendered at the end (right) of the composer toolbar. */
    endAdornment?: React.ReactNode
    /** className applied to the composer container (composer mode only). */
    containerClassName?: string
  }

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      error,
      variant,
      startAdornment,
      endAdornment,
      containerClassName,
      ...props
    },
    ref,
  ) => {
    const innerRef = React.useRef<HTMLTextAreaElement>(null)

    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node
        if (typeof ref === "function") {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref],
    )

    const isComposer = startAdornment != null || endAdornment != null

    if (!isComposer) {
      return (
        <textarea
          className={cn(
            textareaVariants(),
            error &&
              "border-coval-danger-border focus:border-coval-danger-border bg-coval-danger-surface text-coval-danger-on-surface placeholder:text-coval-danger-on-surface/80",
            className,
          )}
          ref={ref}
          {...props}
        />
      )
    }

    return (
      <div
        role="presentation"
        className={cn(
          textareaContainerVariants({ variant }),
          error &&
            "border-coval-danger-border focus-within:border-coval-danger-border bg-coval-danger-surface",
          containerClassName,
        )}
        onClick={(event) => {
          // Don't steal focus from nested interactive adornments (buttons,
          // menu triggers, etc.); only focus the field on bare-container clicks.
          const target = event.target as HTMLElement
          if (
            target.closest(
              "button,a,input,select,textarea,[role='button'],[role='menuitem']",
            )
          ) {
            return
          }
          innerRef.current?.focus()
        }}
      >
        <textarea
          className={cn(
            composerTextareaVariants({ variant }),
            error &&
              "text-coval-danger-on-surface placeholder:text-coval-danger-on-surface/80",
            className,
          )}
          ref={setRefs}
          {...props}
        />
        <div className="mt-1 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">{startAdornment}</div>
          <div className="flex items-center gap-2">{endAdornment}</div>
        </div>
      </div>
    )
  },
)

Textarea.displayName = "Textarea"

export { Textarea, textareaVariants, textareaContainerVariants }
